import React, { useState } from 'react';
import { IDESelectorTargetContainer, IDESelectorDropdown } from './IDESelector.styles';
import { Menu } from '../Menu/Menu';
import { HudIcon, IconType } from '../Icons/HudIcons';
import { useLocalStorage, useClickOutside } from '@mantine/hooks';

export type IDEKey =
  | 'Visual_Studio_Code'
  | 'Cursor'
  | 'Windsurf'
  | 'WS'
  | 'PY'
  | 'PC'
  | 'IU'
  | 'IC';
const DEFAULT_IDE_TO_SHOW = 'Visual_Studio_Code';

// These are the IDE strings as reported by the IDEs themselves
const IDEAssetMap: Record<
  IDEKey,
  {
    icon: IconType;
    name: string;
  }
> = {
  Visual_Studio_Code: { icon: 'ides.vscode', name: 'VSCode' },
  Cursor: { icon: 'ides.cursor', name: 'Cursor' },
  Windsurf: { icon: 'ides.windsurf', name: 'Windsurf' },
  IU: { icon: 'ides.idea', name: 'Idea Ultimate' },
  IC: { icon: 'ides.idea', name: 'Idea CE' },
  WS: { icon: 'ides.webstorm', name: 'WebStorm' },
  PY: { icon: 'ides.pycharm', name: 'PyCharm Professional' },
  PC: { icon: 'ides.pycharm', name: 'PyCharm CE' },
};

export type IDEItem = {
  key: keyof typeof IDEAssetMap;
  marketplaceUrl: string;
  isInstalled: boolean;
  alwaysShow?: boolean;
};

type IDESelectorProps = {
  ideList: IDEItem[];
  onClickIDE?: (ideKey: keyof typeof IDEAssetMap, element: string) => void;
  onClickShowMore?: () => void;
};

export default function IDESelector({
  ideList = [],
  onClickIDE,
  onClickShowMore,
}: IDESelectorProps) {
  const [lastActivatedIde, setLastActivatedIde] = useLocalStorage<
    keyof typeof IDEAssetMap
  >({
    key: 'lastSelectedIde',
    defaultValue: undefined,
  });

  const [lastSelectedIde, setLastSelectedIde] = useState<keyof typeof IDEAssetMap>();

  const [isOpened, setIsOpened] = useState(false);
  const [showAllIDEs, setShowAllIDEs] = useState(false);

  // fix an issue that happens when clicking on the dropdown arrow while the dropdown is open
  const onClickOutside = useClickOutside(() => setIsOpened(false), null, [
    document.querySelector('.dropdownButton'),
    document.querySelector('.mantine-Menu-dropdown'),
  ]);

  function localOnClickIDE(ideKey: keyof typeof IDEAssetMap, element: string) {
    if (ideList.find((ide) => ide.key === ideKey)?.isInstalled !== false) {
      setLastActivatedIde(ideKey);
      setIsOpened(false);
    } else {
      setIsOpened(true);
    }
    onClickIDE?.(ideKey, element);
    setLastSelectedIde(ideKey);
  }

  function localOnClickShowMore() {
    setShowAllIDEs(true);
    onClickShowMore?.();
  }

  function getIDEItem(ideKey: keyof typeof IDEAssetMap) {
    const ideAssets = IDEAssetMap[ideKey];
    if (!ideAssets) {
      console.warn(`IDESelector: IDE key ${ideKey} not found in IDEAssetMap`);
      return null;
    }

    return (
      <Menu.Item
        key={ideKey}
        className="ideItem"
        onClick={(e) => {
          localOnClickIDE(ideKey, 'list');
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <HudIcon size="14px" icon={ideAssets.icon} />
        Open in {ideAssets.name}
      </Menu.Item>
    );
  }
  const IDEsToShow = showAllIDEs
    ? ideList
    : ideList.filter((ide) => ide.isInstalled || ide.alwaysShow);

  const displayShowMoreButton = IDEsToShow.length < ideList.length && !showAllIDEs;

  const ideListElement = (
    <>
      {IDEsToShow.map((ide) => getIDEItem(ide.key))}

      {displayShowMoreButton && (
        <>
          <Menu.Divider />
          <Menu.Item onClick={localOnClickShowMore}>Show More...</Menu.Item>
        </>
      )}
    </>
  );

  let ideNotInstalledElement = null;

  if (lastSelectedIde) {
    const ideNotInstalledAssets = IDEAssetMap[lastSelectedIde];
    const marketplaceUrl = ideList.find(
      (ide) => ide.key === lastSelectedIde,
    )?.marketplaceUrl;
    ideNotInstalledElement = (
      <div className={'ideNotInstalled'}>
        <p>
          Install or update Hud on {ideNotInstalledAssets.name}, then refresh the page
        </p>
        <p>
          <a href={marketplaceUrl} target="_blank">
            Get Hud for {ideNotInstalledAssets.name}
          </a>
        </p>
      </div>
    );
  }

  const isNotInstalled =
    lastSelectedIde && !ideList.find((ide) => ide.key === lastSelectedIde)?.isInstalled;
  const dropdownContent = isNotInstalled ? ideNotInstalledElement : ideListElement;

  const ideToDisplay = (() => {
    const lastActivatedIDEisInvalid = lastActivatedIde && !IDEAssetMap[lastActivatedIde];
    const lastActivatedIDENotInstalled =
      lastActivatedIde &&
      !ideList.find((ide) => ide.key === lastActivatedIde)?.isInstalled;

    // if lastActivatedIDE is not installed or is invalid, use the first installed IDE
    if (!lastActivatedIde || lastActivatedIDEisInvalid || lastActivatedIDENotInstalled) {
      const firstInstalledIdeInList = ideList.find((ide) => ide.isInstalled);
      return firstInstalledIdeInList?.key || DEFAULT_IDE_TO_SHOW;
    }

    // lastActivatedIDE is valid, use it
    return lastActivatedIde;
  })();

  const lastSelectedIdeIcon = IDEAssetMap[ideToDisplay]?.icon;

  return (
    <Menu
      position="bottom-start"
      opened={isOpened}
      closeOnItemClick={false}
      onOpen={() => {
        setShowAllIDEs(false);
      }}
      onClose={() => {
        // fixes a visual jump when closing the dropdown
        setIsOpened(false);
        setTimeout(() => setLastSelectedIde(undefined), 100);
      }}
    >
      <Menu.Target>
        <IDESelectorTargetContainer>
          <button
            className={'latestSelectedIdeButton'}
            onClick={() => localOnClickIDE(ideToDisplay, 'default')}
          >
            {lastSelectedIdeIcon && (
              <HudIcon size="14px" icon={IDEAssetMap[ideToDisplay].icon} />
            )}
          </button>
          <button className={'dropdownButton'} onClick={() => setIsOpened(!isOpened)}>
            <HudIcon size="14px" icon={'menu.caretDown'} />
          </button>
        </IDESelectorTargetContainer>
      </Menu.Target>
      <IDESelectorDropdown ref={onClickOutside}>{dropdownContent}</IDESelectorDropdown>
    </Menu>
  );
}
